import { useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Box, Card, Typography, Button, Chip, Avatar } from '@mui/material';
import DoneAllIcon           from '@mui/icons-material/DoneAllRounded';
import FiberManualRecordIcon from '@mui/icons-material/FiberManualRecord';
import { fetchNotifications, markRead, markAllRead } from '../../app/slices/notificationsSlice';
import { PageHeader }  from '../../components/common/PageHeader';
import { LoadingSpinner } from '../../components/common/LoadingSpinner';
import { EmptyState }  from '../../components/common/EmptyState';
import { timeAgo, formatDateTime } from '../../utils/helpers';

const TYPE_CONFIG = {
  TaskAssigned:      { color: '#3b82f6', bg: '#eff6ff', label: 'Task Assigned' },
  StatusChanged:     { color: '#f59e0b', bg: '#fffbeb', label: 'Status Changed' },
  AssigneeChanged:   { color: '#8b5cf6', bg: '#f5f3ff', label: 'Reassigned' },
  ApprovalRequested: { color: '#ef4444', bg: '#fef2f2', label: 'Approval Requested' },
  ApprovalDecision:  { color: '#10b981', bg: '#f0fdf4', label: 'Approval Decision' },
  TaskCompleted:     { color: '#10b981', bg: '#f0fdf4', label: 'Completed' },
};

export default function NotificationsPage() {
  const dispatch = useDispatch();
  const { list, loading } = useSelector(s => s.notifications);
  const unread = list.filter(n => !n.isRead).length;

  useEffect(() => { dispatch(fetchNotifications()); }, [dispatch]);

  if (loading && !list.length) return <LoadingSpinner />;

  return (
    <Box>
      <PageHeader
        title="Notifications"
        subtitle={unread > 0 ? `${unread} unread` : 'All caught up'}
        action={unread > 0 && (
          <Button variant="outlined" size="small" startIcon={<DoneAllIcon />}
            onClick={() => dispatch(markAllRead())}>
            Mark All Read
          </Button>
        )}
      />
      {list.length === 0 ? <EmptyState message="No notifications yet." /> : (
        <Box display="flex" flexDirection="column" gap={1}>
          {list.map(n => {
            const cfg = TYPE_CONFIG[n.type] || { color: '#64748b', bg: '#f8fafc', label: n.type };
            return (
              <Card key={n.id}
                onClick={() => !n.isRead && dispatch(markRead(n.id))}
                sx={{
                  p: 2, cursor: n.isRead ? 'default' : 'pointer',
                  opacity: n.isRead ? 0.65 : 1,
                  borderLeft: `3px solid ${n.isRead ? 'transparent' : cfg.color}`,
                  transition: 'all 0.15s',
                  '&:hover': { boxShadow: n.isRead ? undefined : '0 2px 12px rgba(0,0,0,0.08)' },
                }}>
                <Box display="flex" alignItems="flex-start" gap={1.5}>
                  {!n.isRead && (
                    <FiberManualRecordIcon sx={{ fontSize: 8, color: cfg.color, mt: 0.9, flexShrink: 0 }} />
                  )}
                  <Box flex={1}>
                    <Typography variant="body2" fontWeight={n.isRead ? 400 : 600}>
                      {n.message}
                    </Typography>
                    <Box display="flex" gap={1} mt={0.75} alignItems="center" flexWrap="wrap">
                      <Chip
                        label={cfg.label} size="small"
                        sx={{ fontSize: '0.65rem', height: 18, bgcolor: cfg.bg, color: cfg.color, fontWeight: 600 }}
                      />
                      {/* timeAgo uses local time via our fixed helpers.js */}
                      <Typography variant="caption" color="text.secondary">
                        {timeAgo(n.createdAt)}
                      </Typography>
                      <Typography variant="caption" color="text.secondary" sx={{ opacity: 0.6 }}>
                        · {formatDateTime(n.createdAt)}
                      </Typography>
                    </Box>
                  </Box>
                </Box>
              </Card>
            );
          })}
        </Box>
      )}
    </Box>
  );
}
